import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { CONTACT } from "@/lib/content";

const LINKS = [
  { label: "Services", to: "/#services" },
  { label: "Finishes", to: "/finishes" },
  { label: "Process", to: "/process" },
  { label: "Gallery", to: "/gallery" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 350);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
      window.scrollTo({ top: 0 });
    }
  };

  const goQuote = () => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" }), 350);
    } else {
      document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-[100] transition-all duration-500"
      style={{
        padding: scrolled ? "14px 0" : "24px 0",
        background: scrolled ? "rgba(12,11,9,0.86)" : "transparent",
        backdropFilter: scrolled ? "blur(14px)" : "none",
        borderBottom: `1px solid ${scrolled ? "var(--line)" : "transparent"}`,
      }}
    >
      <nav className="wrap flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("/")}
          className="flex items-center gap-3 font-serif text-[20px]"
          style={{ color: "var(--champagne)" }}
        >
          <span
            className="inline-block"
            style={{
              width: 9,
              height: 12,
              borderRadius: "0 60% 60% 60%",
              background: "linear-gradient(160deg,var(--brass-bright),var(--copper))",
              transform: "rotate(-45deg)",
              boxShadow: "0 0 10px rgba(201,162,75,0.6)",
            }}
          />
          <span className="tracking-[0.12em]">LUXEBOND</span>
        </button>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => go(l.to)}
              className="font-mono-lbl text-[11px] transition-colors"
              style={{ color: "var(--stone)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--champagne)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--stone)")}
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-quote-cta"
            onClick={goQuote}
            className="btn btn-ghost !py-[10px] !px-[22px] !text-[11px]"
            style={{ borderColor: "var(--brass)", color: "var(--brass-bright)" }}
          >
            Get a Quote
          </button>
        </div>

        <button
          data-testid="nav-burger"
          className="md:hidden"
          style={{ color: "var(--champagne)" }}
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden wrap flex flex-col gap-5 pt-8 pb-4"
        >
          {LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => go(l.to)}
              className="font-mono-lbl text-[13px] text-left"
              style={{ color: "var(--stone)" }}
            >
              {l.label}
            </button>
          ))}
          <button onClick={goQuote} className="btn btn-primary w-full justify-center mt-2">
            Get a Quote
          </button>
          <a href={CONTACT.phoneHref} className="font-mono-lbl text-[12px]" style={{ color: "var(--brass-bright)" }}>
            {CONTACT.phone}
          </a>
        </div>
      )}
    </header>
  );
}
